import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import {LoadingButton} from '@mui/lab';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import {forEach, isEmpty} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as TooltipRecharts,
  XAxis,
  YAxis,
} from 'recharts';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {usePrint} from '../../../hooks/print';
import {useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {
  DashboardPanelData,
  PanelFilterParams,
} from '../../../interfaces/Dashboard';
import {
  MachineDetailLineReportQuery,
  MachineDetailLineReportResponse,
} from '../../../interfaces/MachineDetailLineReport';
import reduxSelectors from '../../../redux/selectors';
import {CHART_COLORS} from '../../../utils/colors';
import {getDaysArrayBetween} from '../../../utils/datetime';
import {saveFile} from '../../../utils/file';
import {isPresent} from '../../../utils/type-guards';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DraggablePagination from '../../common/DraggablePagination';
import {useDashboardDrilldown} from '../../dashboards/DashboardContext';
import {usePanelFilter} from '../../dashboards/entities/DashboardEntityContext';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import {MachineInputSelectV2} from '../../selectors/MachineInputSelectV2';
import {MachineTypeSelect} from '../../selectors/MachineTypeSelect';
import {ShiftSelect} from '../../selectors/ShiftSelect';
import {ZoneSelect} from '../../selectors/ZoneSelect';
import DrilldownMenu, {MenuState} from './DrilldownMenu';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value: DashboardPanelData) => void;
}

const MachineDetailsReportLine = (props: Props) => {
  const {filter, setFilter} = usePanelFilter();

  /*************/
  /* pagination */
  /*************/
  const [page, setPage] = useState<number>(
    filter.page ?? props.value?.page ?? 0
  );
  const [limit, setLimit] = useState<number>(
    filter.limit ?? props.value?.limit ?? 20
  );
  const [position, setPosition] = useState<
    {x: number; y: number} | undefined
  >(filter.position ?? props.value?.position ?? undefined);

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] =
    useState<MachineDetailLineReportResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const isEmptyResults = isEmpty(fetchedData?.results);

  const params = useMemo<MachineDetailLineReportQuery>(() => {
    const filterParams = filter.params ?? {};
    const propsParams = props.value?.params ?? {};

    return {
      type_id:
        'type_id' in filterParams
          ? filterParams.type_id
          : propsParams.type_id ?? null,
      shift_id:
        'shift_id' in filterParams
          ? filterParams.shift_id
          : propsParams.shift_id ?? null,
      zone_id:
        'zone_id' in filterParams
          ? filterParams.zone_id
          : propsParams.zone_id ?? null,
      date_start:
        filterParams.date_start ??
        propsParams.date_start ??
        dayjs().format('YYYY-MM-DD'),
      date_end:
        filterParams.date_end ??
        propsParams.date_end ??
        dayjs().format('YYYY-MM-DD'),
      input: filterParams.input ?? propsParams.input ?? {},
      page,
      limit,
    };
  }, [filter.params, page, limit]);

  const fetchData = useCallback(
    async (params: MachineDetailLineReportQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/report/machine-details-line`;
        const resp = await API.get<MachineDetailLineReportResponse>(endpoint, {
          params: {
            ...params,
            input: JSON.stringify(params.input),
          },
        });
        setFetchedData(resp.data);
        setHasPendingChanges(false);
        setIsFirstLoad(false);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_481bd457');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  const showPendingChangesWarning = useMemo(
    () => hasPendingChanges && filter.autoReload === false,
    [hasPendingChanges, filter.autoReload]
  );

  const handleRefresh = useCallback(() => {
    if (params) {
      fetchData(params);
      setHasPendingChanges(false);
    }
  }, [params, fetchData]);

  const handleToggleAutoReload = useCallback(() => {
    const isCurrentlyEnabled = filter.autoReload !== false;
    setFilter(
      update(filter, {
        autoReload: {
          $set: !isCurrentlyEnabled,
        },
      })
    );
  }, [filter, setFilter]);

  useEffect(() => {
    if (params) {
      if (filter.autoReload !== false || isFirstLoad) {
        fetchData(params);
      } else if (!isFirstLoad) {
        setHasPendingChanges(true);
      }
      props.onUpdate?.({
        ...props.value,
        chartType: 'line',
        params,
        autoReload: filter.autoReload,
      });
    }
  }, [params, filter.autoReload]);

  /****************/
  /* auto refresh */
  /****************/
  const [refreshInterval, setRefreshInterval] = useRefreshInterval(
    () => fetchData(params),
    props.value?.refreshInterval
  );

  useEffect(() => {
    props.onUpdate?.({...props.value, chartType: 'line', refreshInterval});
  }, [refreshInterval]);

  useEffect(() => {
    if (filter.page !== undefined && filter.page !== page) {
      setPage(filter.page);
    }
    if (filter.limit !== undefined && filter.limit !== limit) {
      setLimit(filter.limit);
    }
    if (filter.position !== undefined) {
      setPosition(filter.position);
    }
  }, [filter.page, filter.limit, filter.position]);

  useEffect(() => {
    setFilter({
      ...filter,
      page,
      limit,
      position,
    });
    props.onUpdate?.({
      ...props.value,
      page,
      limit,
      position,
    });
  }, [page, limit, position]);

  useEffect(() => {
    setPage(0);
  }, [params.type_id, params.shift_id, params.zone_id, params.date_start, params.date_end, params.input]);

  /*********/
  /* chart */
  /*********/
  const theme = useTheme();

  const lines = useMemo(() => {
    const labels = fetchedData?.metadata?.labels || {};
    return Object.keys(labels).map((externalId) => ({
      key: externalId,
      label: fetchedData?.metadata?.labels[externalId],
    }));
  }, [fetchedData]);

  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);
  const [hoveredLine, setHoveredLine] = useState<string | null>(null);

  const toggleKey = (key: string) => {
    setHiddenKeys(
      hiddenKeys.includes(key)
        ? hiddenKeys.filter((i) => i !== key)
        : [...hiddenKeys, key]
    );
  };

  const handleLineMouseEnter = useCallback((key: string) => {
    setHoveredLine(key);
  }, []);

  const handleLineMouseLeave = useCallback(() => {
    setHoveredLine(null);
  }, []);

  const handleLegendClick = useCallback(
    (item: any) => {
      toggleKey(item.dataKey);
    },
    [hiddenKeys]
  );

  const dataset = useMemo(() => {
    const defaultValue: any = {};
    const rawApiData = fetchedData?.results?.map((item) => {
      forEach(Object.keys(item), (key) => {
        defaultValue[key] = 0;
      });
      return {
        ...item,
        name: item?.period,
        period: undefined,
      };
    });
    const daylist = getDaysArrayBetween(params.date_start!, params.date_end!);
    return daylist.map((day) => ({
      name: day,
      ...defaultValue,
      ...rawApiData?.find((item) => String(item.name) === day),
    }));
  }, [fetchedData, params]);

  const maxValue = useMemo(() => {
    if (!dataset || dataset.length === 0) {
      return 0;
    }
    const allValues = dataset.flatMap((dataPoint) =>
      Object.keys(dataPoint)
        .filter(
          (k) => k !== 'name' && k !== 'period' && !hiddenKeys.includes(k)
        )
        .map((k) => dataPoint[k])
    );
    return Math.max(...allValues);
  }, [dataset, hiddenKeys]);

  const renderTooltipContent = useCallback(
    ({active, payload, label}: any) => {
      if (active && payload && payload.length > 0) {
        if (!hoveredLine) {
          return null;
        }

        const activeItem = payload.find((p: any) => p.dataKey === hoveredLine);
        if (!activeItem) {
          return null;
        }

        const threshold = maxValue * 0.05;

        const closeDots = payload
          .filter((p: any) => Math.abs(p.value - activeItem.value) <= threshold)
          .sort((a: any, b: any) => b.value - a.value);

        return (
          <Box
            sx={{
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1,
            }}
          >
            <Box sx={{color: theme.palette.text.secondary}}>{label}</Box>
            {closeDots.map((item: any, idx: number) => (
              <Box
                key={idx}
                sx={{
                  color: item.color,
                  fontWeight: item.dataKey === hoveredLine ? 'bold' : 'normal',
                  fontStyle: item.dataKey === hoveredLine ? 'italic' : 'normal',
                }}
              >
                {item.name}: {item.value}
              </Box>
            ))}
          </Box>
        );
      }
      return null;
    },
    [hoveredLine, theme, maxValue]
  );

  /*********/
  /* print */
  /*********/
  const chartRef = useRef<HTMLDivElement>();
  const [printChart] = usePrint(chartRef.current);
  const printTheme = useAppSelector(({app}) => app.printTheme);

  /**********/
  /* export */
  /**********/
  const [exportInProgress, setExportInProgress] = useState(false);

  const {
    shifts,
    zones,
    machine_types: machineTypes,
    machine_inputs: machineInputs,
  } = useAppSelector(reduxSelectors.assets.getAssets);

  const submitExport = async (params: MachineDetailLineReportQuery) => {
    setExportInProgress(true);
    try {
      const endpoint = `${apiBaseUrl}/report/machine-details-export`;
      const resp = await API.get(endpoint, {
        params: {
          ...params,
          input: JSON.stringify(params.input),
          export_type: 'line',
        },
        responseType: 'blob',
      });

      const fileNameParts = [
        'machineDetailsLine',
        `${params.date_start}__${params.date_end}`,
        shifts.find((i) => i.id === params.shift_id)?.name?.replace(' ', '-'),
        zones.find((i) => i.id === params.zone_id)?.name?.replace(' ', '-'),
        machineTypes
          .find((i) => i.id === params.type_id)
          ?.name?.replace(' ', '-'),

        machineInputs
          .map((i) => {
            const v = params.input?.[i.id];
            if (v === 1) {
              return `${i.name.replace(' ', '-')}-is-on`;
            } else if (v === 0) {
              return `${i.name.replace(' ', '-')}-is-off`;
            }
          })
          .filter(isPresent)
          .join('_'),
      ].filter(isPresent);

      const fileName = `${fileNameParts.join('__')}.csv`;

      saveFile(resp.data, fileName);
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'STRA_8c452fe2');
      setFetchedErrors(messages);
    }
    setExportInProgress(false);
  };

  const drilldown = useDashboardDrilldown();
  const [menuState, setMenuState] = useState<MenuState>({
    visible: false,
    mouseX: 0,
    mouseY: 0,
  });

  // Click on active dot doesn't return proper mouse position so we need to track it manually
  const [mousePos, setMousePos] = useState<any>({});
  useEffect(() => {
    const handleMouseMove = (event: any) =>
      setMousePos({x: event.clientX, y: event.clientY});
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const openDrilldown = useCallback(
    (externalId: number, event: any) => {
      setMenuState?.(
        update(menuState, {
          visible: {$set: true},
          mouseY: {$set: event.clientY || mousePos.y},
          mouseX: {$set: event.clientX || mousePos.x},
          params: {
            $set: {
              ...params,
              machines: [externalId],
              external_id: externalId,
              section_id: params.zone_id,
            },
          },
        })
      );
    },
    [menuState, mousePos, params]
  );

  const handleDrilldown = (
    state: MenuState,
    type: 'history' | 'interactions' | 'summary'
  ) => {
    if (type === 'history') {
      drilldown({
        code: 'MachineHistoryReport',
        chartType: 'bar',
        filter: state.params as PanelFilterParams,
      });
    } else if (type === 'interactions') {
      drilldown({
        code: 'MachineInteractionReport',
        chartType: 'bar',
        filter: state.params as PanelFilterParams,
      });
    } else {
      drilldown({
        code: 'MachineSummaryReport',
        chartType: 'bar',
        filter: state.params as PanelFilterParams,
      });
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      gap={1}
      width="100%"
      overflow="hidden"
    >
      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" flexDirection="column">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="start"
          gap={1}
        >
          <Box display="flex" overflow="auto" py={1} gap={1}>
            <MachineTypeSelect
              value={params.type_id}
              size="small"
              sx={{minWidth: 200}}
              onChange={(v) => {
                setFilter(
                  update(filter, {
                    params: {
                      type_id: {
                        $set: v,
                      },
                    },
                  })
                );
              }}
            />

            <Box minWidth={400}>
              <DateRangeSelect
                value={[
                  dayjs(params.date_start).toDate(),
                  dayjs(params.date_end).toDate(),
                ]}
                size="small"
                onChange={(v) => {
                  setFilter(
                    update(filter, {
                      params: {
                        date_start: {
                          $set: v?.[0]
                            ? dayjs(v?.[0]).format('YYYY-MM-DD')
                            : undefined,
                        },
                        date_end: {
                          $set: v?.[1]
                            ? dayjs(v?.[1]).format('YYYY-MM-DD')
                            : undefined,
                        },
                      },
                    })
                  );
                }}
              />
            </Box>

            <ShiftSelect
              value={params?.shift_id}
              size="small"
              nullLabel="All Shifts"
              sx={{minWidth: 200}}
              onChange={(v) => {
                setFilter(
                  update(filter, {
                    params: {
                      shift_id: {
                        $set: v,
                      },
                    },
                  })
                );
              }}
            />

            <ZoneSelect
              value={params?.zone_id}
              size="small"
              nullLabel="All Sections"
              sx={{minWidth: 200}}
              onChange={(v) => {
                setFilter(
                  update(filter, {
                    params: {
                      zone_id: {
                        $set: v,
                      },
                    },
                  })
                );
              }}
            />

            {/* <MachineInputFilterSelect
              value={filter.params?.input}
              size="small"
              fullWidth
              onChange={(v) => {
                onChange?.(
                  update(filter, {
                    params: {
                      input: {
                        $set: v,
                      }
                    },
                  })
                )
              }}
            /> */}

            <Box width={250}>
              <MachineInputSelectV2
                value={params?.input}
                label="Inputs"
                size="small"
                onChange={(v) => {
                  setFilter(
                    update(filter, {
                      params: {
                        input: {
                          $set: v,
                        },
                      },
                    })
                  );
                }}
              />
            </Box>
          </Box>

          <Box display="flex" py={1}>
            <ButtonGroup size="small" sx={{height: 40}}>
              <Tooltip title="Reload">
                <Button onClick={handleRefresh}>
                  <RefreshIcon />
                </Button>
              </Tooltip>

              <Tooltip
                title={
                  filter.autoReload === false
                    ? 'Auto-reload disabled: Use refresh button to apply filter changes'
                    : 'Auto-reload enabled: Report reloads when filters change'
                }
              >
                <Button
                  size="small"
                  variant={
                    filter.autoReload === false ? 'outlined' : 'contained'
                  }
                  onClick={handleToggleAutoReload}
                  sx={{minWidth: 'auto'}}
                >
                  <SyncIcon />
                </Button>
              </Tooltip>

              <AutoRefreshSelect
                value={props.value?.refreshInterval}
                onChange={setRefreshInterval}
              />

              <Tooltip title="Export to Excel">
                <LoadingButton
                  size="small"
                  loading={exportInProgress}
                  variant="outlined"
                  onClick={() => submitExport(params)}
                >
                  <FileDownloadIcon />
                </LoadingButton>
              </Tooltip>

              <Tooltip title="Print">
                <Button onClick={() => printChart()}>
                  <PrintIcon />
                </Button>
              </Tooltip>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>

      {showPendingChangesWarning && (
        <Alert severity="warning" sx={{mb: 1}}>
          Auto-reload is disabled. Click the refresh button to apply filter
          changes.
        </Alert>
      )}

      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      <Box
        ref={chartRef}
        overflow="hidden"
        position="relative"
        display="flex"
        flexDirection="column"
        gap={3}
        alignItems="center"
        justifyContent="center"
        height="100%"
        width="100%"
      >
        <DraggablePagination
          page={page}
          pageSize={limit}
          totalItems={fetchedData?.total ?? 0}
          position={position}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          onPositionChange={setPosition}
        />

        <Box display="none" displayPrint="block">
          Machine Stoppages for Machine Types by Period - {params.date_start} to{' '}
          {params.date_end}
        </Box>

        <Box
          height={printTheme ? '500px' : '100%'}
          width={printTheme ? '700px' : '100%'}
        >
          <DrilldownMenu
            onItemClick={handleDrilldown}
            menuState={menuState}
            setMenuState={setMenuState}
          >
            {!isEmptyResults ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dataset}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <TooltipRecharts
                    content={renderTooltipContent}
                    cursor={false}
                  />
                  <Legend onClick={handleLegendClick} />

                  {lines.map(({key, label}, index) => (
                    <Line
                      onClick={(_data, event) =>
                        openDrilldown(parseInt(key), event)
                      }
                      key={key}
                      dataKey={key}
                      name={label}
                      hide={hiddenKeys.includes(key)}
                      stroke={CHART_COLORS[index]}
                      strokeWidth={hoveredLine === key ? 4 : 2}
                      isAnimationActive={!printTheme}
                      onMouseEnter={() => handleLineMouseEnter(key)}
                      onMouseLeave={handleLineMouseLeave}
                      activeDot={{
                        onClick: (_data, event) =>
                          openDrilldown(parseInt(key), event),
                        onMouseEnter: () => handleLineMouseEnter(key),
                        onMouseLeave: handleLineMouseLeave,
                        r: 6,
                      }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Box
                display="flex"
                height="100%"
                alignItems="center"
                justifyContent="center"
              >
                {fetchedInProgress ? 'Loading...' : 'No data to display'}
              </Box>
            )}
          </DrilldownMenu>
        </Box>
      </Box>
    </Box>
  );
};

export default MachineDetailsReportLine;
